import Image from 'next/image';
import { FC, CSSProperties, useState, useRef, useEffect } from 'react';
import { ArrowAngle } from 'components/library/svgs--foldertentative/arrow-angle';
import useClientSideMediaQuery from 'hooks/use-client-side-media-query';
import {
  Play,
  Pause,
} from 'components/library/svgs--foldertentative/play-pause';
import {
  ScrollerWrapper,
  MarqueeWrapper,
  ScrollerButton,
  Frame,
  ItemWrap,
} from './marquee.styled';

export type scrollerTypes = 'simple' | 'double';
export type directions = 'left' | 'right';
export type ratios = '14:9' | '1:1' | '9:14';
export type shapes = 'round' | 'vertical' | 'horizontal' | 'horizontal-pill';
export interface ItemProps {
  cover?: {
    src: string;
    width: number;
    height: number;
    alt: string;
  };
  video?: {
    src: string;
    format?: string;
    title?: string;
  };
  videoLoop?: boolean;
  url?: string;
  ratio?: ratios | undefined;
  shape?: shapes | undefined;
}

interface MarqueeProps {
  items: ItemProps[];
  direction: directions;
  type: scrollerTypes;
}

interface MarqueeItemProps {
  items: ItemProps[];
  type: scrollerTypes;
  visible: boolean;
}

const MarqueeItem: FC<MarqueeItemProps> = ({ items, visible, type }) => {
  const [isPlaying, setIsPlaying] = useState<boolean[]>(items.map(() => false));
  const videoRefs = useRef<Array<HTMLVideoElement | null>>([]);

  const ratios = items.map((img: ItemProps) => {
    switch (img?.shape) {
      case 'horizontal':
        return '14/9';
      case 'horizontal-pill':
        return '14/9';
      case 'round':
        return '1/1';
      default:
        return '9/14';
    }
  });

  const doubleWidths = items.map((img: ItemProps) => {
    switch (img?.ratio) {
      case '14:9':
        return '270';
      case '1:1':
        return '260';
      case '9:14':
        return '238';
      default:
        return '238';
    }
  });

  const simpleWidths = items.map((img: ItemProps) => {
    switch (img?.shape) {
      case 'round':
        return '430';
      case 'vertical':
        return '276';
      case 'horizontal':
        return '322';
      default:
        return '322';
    }
  });

  const handleVideoClick = (index: number) => {
    const current = videoRefs.current[index];
    if (current) {
      setIsPlaying((prev) => {
        const next = [...prev];
        if (!prev[index]) {
          current.play();
          next[index] = true;
        } else {
          current.pause();
          next[index] = false;
        }
        return next;
      });
    }
  };

  return (
    <ItemWrap type={type} aria-hidden={!visible}>
      {items.map((item: ItemProps, index: number) => {
        const ratio = item?.ratio
          ? item?.ratio.replace(':', '/')
          : ratios[index];

        const width =
          type == 'simple' ? simpleWidths[index] : doubleWidths[index];

        return (
          <Frame
            key={index}
            as={item.url ? 'a' : 'div'}
            href={item.url}
            target='_blank'
            style={
              {
                '--ratio': ratio,
                '--width': `${width}px`,
              } as CSSProperties
            }
            shape={item?.shape}
            type={type}
          >
            {item.url && (
              <ScrollerButton>
                <ArrowAngle color='#fcfcf7' />
              </ScrollerButton>
            )}
            {item.video && !item.videoLoop && (
              <ScrollerButton onClick={() => handleVideoClick(index)}>
                {isPlaying[index] ? (
                  <Pause color='#fcfcf7' />
                ) : (
                  <Play color='#fcfcf7' />
                )}
              </ScrollerButton>
            )}
            {item?.video && (
              <video
                ref={(el) => {
                  videoRefs.current[index] = el;
                }}
                width='1920'
                height='1080'
                {...(item.videoLoop
                  ? {
                      muted: true,
                      autoPlay: true,
                      playsInline: true,
                      loop: true,
                    }
                  : {})}
                title={item.video?.title}
                onEnded={() =>
                  setIsPlaying((prev) => {
                    const next = [...prev];
                    next[index] = false;
                    return next;
                  })
                }
              >
                <source src={item.video.src} type='video/mp4' />
                Your browser does not support the video tag.
              </video>
            )}
            {item.cover && (
              <Image
                key={index}
                src={item.cover.src}
                width={item.cover.width}
                height={item.cover.height}
                alt={item.cover.alt}
              />
            )}
          </Frame>
        );
      })}
    </ItemWrap>
  );
};

const Marquee: FC<MarqueeProps> = ({ items, direction, type }) => {
  const isSmall = useClientSideMediaQuery(960);
  const marquee = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number>(0);
  const offset = `-${width / 2}px`;
  const xPos =
    direction == 'left' ? { from: 0, to: offset } : { from: offset, to: 0 };

  const resize = () => {
    const { width } = marquee.current.getBoundingClientRect();
    setWidth(width);
  };

  useEffect(() => {
    resize();

    window.addEventListener('resize', resize);

    return () => {
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <ScrollerWrapper type={type} className='marquee'>
      <MarqueeWrapper pos={xPos} ref={marquee} speed={items.length * 2 * 3}>
        <MarqueeItem type={type} visible={false} items={items} />
        {!isSmall && <MarqueeItem type={type} visible={true} items={items} />}
      </MarqueeWrapper>
    </ScrollerWrapper>
  );
};

export default Marquee;
